import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { Button } from '@/components/ButtonComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { DevDemoStackParamList } from '@/feature/devsettings/DevDemoStack';
import { withOpacity } from '@/styles/styles';

type Nav = NativeStackNavigationProp<DevDemoStackParamList>;

type DemoMeta = {
    label: string;
    presentation: string;
    animation: string;
    gestureEnabled: boolean;
    stackingNote: string;
};

const DEMO_META: Record<string, DemoMeta> = {
    NavDemoCard: {
        label: 'Card (default)',
        presentation: 'card',
        animation: 'Slides in from the right',
        gestureEnabled: true,
        stackingNote: 'Group A: push stack - stacks freely on other cards. Only type that slides from the right.',
    },
    NavDemoModal: {
        label: 'Modal',
        presentation: 'modal',
        animation: 'Slides up from the bottom (system-controlled, cannot be customized)',
        gestureEnabled: true,
        stackingNote:
            'Group B: native sheet. The screen peeking behind is unmounted in React after the transition (native views stay visible). Try the buttons below: chaining another modal works; pushing a card sends it below the modal chain (dismiss to find it); pushing without an explicit presentation accidentally promotes it to a modal instead of a card.',
    },
    NavDemoPageSheet: {
        label: 'Page Sheet',
        presentation: 'pageSheet',
        animation: 'Slides up from the bottom (system-controlled)',
        gestureEnabled: true,
        stackingNote: 'Group B: native sheet, page-height. Same stacking behavior as modal.',
    },
    NavDemoFormSheet: {
        label: 'Form Sheet',
        presentation: 'formSheet',
        animation: 'Slides up from the bottom (system-controlled)',
        gestureEnabled: true,
        stackingNote: 'Group B: compact sheet.',
    },
    NavDemoFormSheetDetents: {
        label: 'Form Sheet with Detents',
        presentation: 'formSheet',
        animation: 'Slides up from the bottom (system-controlled)',
        gestureEnabled: true,
        stackingNote:
            'Group B: formSheet with two detents (30% / 80%). Scroll view scrolls independently. Drag the grabber to change detent.',
    },
    NavDemoFullScreen: {
        label: 'Full Screen Modal',
        presentation: 'fullScreenModal',
        animation: 'Slides up from the bottom',
        gestureEnabled: false,
        stackingNote: 'Group C: full coverage, no peeking.',
    },
    NavDemoTransparentModal: {
        label: 'Transparent Modal',
        presentation: 'transparentModal',
        animation: 'Slides up from the bottom',
        gestureEnabled: false,
        stackingNote:
            'Group D: transparent overlay - screen behind stays fully visible. Needs an explicit close button.',
    },
    NavDemoContainedModal: {
        label: 'Contained Modal',
        presentation: 'containedModal',
        animation: 'Default',
        gestureEnabled: false,
        stackingNote: 'Group E: contained within parent view bounds.',
    },
    NavDemoContainedTransparent: {
        label: 'Contained Transparent Modal',
        presentation: 'containedTransparentModal',
        animation: 'Default',
        gestureEnabled: false,
        stackingNote: 'Group D: transparent overlay, within parent view bounds.',
    },
    NavDemoAnimFade: {
        label: 'Animation: fade',
        presentation: 'card',
        animation: 'Fades in',
        gestureEnabled: true,
        stackingNote: 'Animation variant',
    },
    NavDemoAnimNone: {
        label: 'Animation: none',
        presentation: 'card',
        animation: 'No animation (instant)',
        gestureEnabled: true,
        stackingNote: 'Animation variant',
    },
    NavDemoAnimSimplePush: {
        label: 'Animation: simple_push',
        presentation: 'card',
        animation: 'Slides from the right without the native header shadow (iOS only)',
        gestureEnabled: true,
        stackingNote: 'Animation variant - iOS only',
    },
    NavDemoAnimSlideFromBottom: {
        label: 'Animation: slide_from_bottom',
        presentation: 'card',
        animation: 'Slides up from the bottom',
        gestureEnabled: true,
        stackingNote: 'Animation variant',
    },
    NavDemoAnimFadeFromBottom: {
        label: 'Animation: fade_from_bottom',
        presentation: 'card',
        animation: 'Fades in from the bottom',
        gestureEnabled: true,
        stackingNote: 'Animation variant',
    },
    NavDemoCardImplicit: {
        label: 'Card - no presentation set (gotcha)',
        presentation: 'none (omitted)',
        animation: 'Default',
        gestureEnabled: true,
        stackingNote:
            'Without an explicit presentation, React Navigation promotes this to a modal when one is already open',
    },
    NavDemoAnimSlideFromRight: {
        label: 'Animation: slide_from_right',
        presentation: 'card',
        animation: 'Slides in from the right (Android only - same as default on iOS)',
        gestureEnabled: true,
        stackingNote: 'Animation variant - Android only',
    },
};

const MODAL_ROUTES = new Set([
    'NavDemoModal',
    'NavDemoPageSheet',
    'NavDemoFormSheet',
    'NavDemoFormSheetDetents',
    'NavDemoFullScreen',
    'NavDemoTransparentModal',
    'NavDemoContainedModal',
    'NavDemoContainedTransparent',
]);

const TRANSPARENT_ROUTES = new Set(['NavDemoTransparentModal', 'NavDemoContainedTransparent']);

const PresentationDemoScreenContent = () => {
    const navigation = useNavigation<Nav>();
    const route = useRoute();
    const [cardPushed, setCardPushed] = useState(false);
    const [cardImplicitPushed, setCardImplicitPushed] = useState(false);
    useEffect(() => {
        if (route.name !== 'NavDemoFormSheetDetents') return;
        return navigation.addListener('sheetDetentChange', e => {
            console.log('[detent]', e.data);
        });
    }, [navigation, route.name]);

    const meta = DEMO_META[route.name] ?? {
        label: route.name,
        presentation: 'unknown',
        animation: 'unknown',
        gestureEnabled: true,
        stackingNote: '',
    };
    const isTransparent = TRANSPARENT_ROUTES.has(route.name);
    const isModal = MODAL_ROUTES.has(route.name);
    const insets = useSafeAreaInsets();

    return (
        <View
            collapsable={false}
            style={[
                styles.container,
                { paddingTop: insets.top, paddingLeft: insets.left, paddingRight: insets.right },
                isTransparent ? styles.transparentRoot : undefined,
            ]}
        >
            <ScreenNavBar title={meta.label} closeIconMode="back" />
            <ScrollView style={styles.scrollView} contentContainerStyle={styles.scroll} nestedScrollEnabled>
                <Box
                    borderRadius="r8"
                    padding="s16"
                    gap="s8"
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                    style={isTransparent ? styles.transparentCard : undefined}
                >
                    <Row label="Presentation" value={meta.presentation} />
                    <Row label="Animation" value={meta.animation} />
                    <Row label="Swipe to dismiss (iOS only)" value={meta.gestureEnabled ? 'Yes' : 'No'} />
                </Box>

                {meta.stackingNote ? (
                    <Box mt="s16">
                        <Text variant="labelSmall" color="neutral400" mb="s4">
                            Stacking behavior (iOS)
                        </Text>
                        <Text variant="bodySmall" color={ds.colorToken.text.primary}>
                            {meta.stackingNote}
                        </Text>
                    </Box>
                ) : null}

                {isModal ? (
                    <Box mt="s24" gap="s12">
                        <Button label="Chain another modal on top ✓" onPress={() => navigation.push('NavDemoModal')} />

                        <Button
                            label={
                                cardPushed
                                    ? '✓ Card pushed - dismiss this modal to reveal it'
                                    : 'Push card (goes below modal chain) ✗'
                            }
                            onPress={() => {
                                navigation.push('NavDemoCard');
                                setCardPushed(true);
                            }}
                        />

                        <Button
                            label={
                                cardImplicitPushed
                                    ? '✓ Opened as modal (no presentation set)'
                                    : 'Push card without presentation set (gotcha) !'
                            }
                            onPress={() => {
                                navigation.push('NavDemoCardImplicit');
                                setCardImplicitPushed(true);
                            }}
                        />
                    </Box>
                ) : null}

                <Box mt="s24">
                    <Button label="Go back" onPress={() => navigation.goBack()} />
                </Box>
            </ScrollView>
        </View>
    );
};

export const PresentationDemoScreen = () => (
    <SafeAreaProvider>
        <PresentationDemoScreenContent />
    </SafeAreaProvider>
);

const Row = ({ label, value }: { label: string; value: string }) => (
    <Box gap="s4">
        <Text variant="labelMedium" color="neutral300">
            {label}
        </Text>
        <Text variant="bodySmall" color={ds.colorToken.text.primary}>
            {value}
        </Text>
    </Box>
);

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: ds.colorValue.background.neutral.base,
    },
    scrollView: {
        flex: 1,
    },
    scroll: {
        padding: 16,
    },
    transparentRoot: {
        backgroundColor: withOpacity(ds.colorValue.background.neutral.base, 0.8),
    },
    transparentCard: {
        backgroundColor: withOpacity(ds.colorValue.background.neutral.base, 0.88),
    },
});
